import { useCallback, useEffect, useState, useRef } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { DateRange } from '../../types/dateRange';
import {
  Box,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Typography
} from '@mui/material';
import { StickyNote2, Person, CalendarToday } from '@mui/icons-material';
import Title from '../../components/Title';
import moment from 'moment';
import {
  getScooterNotesAsync,
  selectNotes
} from '../../redux/slices/scooterNotesSlice';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { Moment } from 'moment';
import FilterHeader, {
  FilterHeaderValues
} from '../../components/Header/FilterHeader';
import { TableCell as MuiTableCell } from '@mui/material';

const DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const Root = styled('div')(({ theme }) => ({
  padding: '0',
  minHeight: 'calc(100vh - 180px)'
}));

const FilterSection = styled(Box)(({ theme }) => ({
  marginBottom: 24
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)'}`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
  padding: '12px 16px'
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: 8,
  backgroundColor: 'rgba(33, 150, 243, 0.12)'
}));

const PaginationWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 24
}));

const initialValues = {
  scooterName: '',
  adminLogin: '',
  dateRange: [null, null] as DateRange<Moment | null>
};

const NotesPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const { limit, total, items } = useSelector(selectNotes);
  const adminInfo = useSelector(selectAdmins);
  const [page, setPage] = useState(1);
  const [filters, setFilters] =
    useState<Partial<FilterHeaderValues>>(initialValues);
  
  const prevCityRef = useRef(adminInfo.currentCity);
  const prevFiltersRef = useRef(filters);

  const onChangePage = useCallback((e: any, nextPage: number) => {
    setPage(nextPage);
  }, []);

  useEffect(() => {
    const cityChanged = prevCityRef.current !== adminInfo.currentCity;
    const filtersChanged = prevFiltersRef.current !== filters;
    const effectivePage = (cityChanged || filtersChanged) ? 1 : page;
    
    prevCityRef.current = adminInfo.currentCity;
    prevFiltersRef.current = filters;
    
    if (effectivePage === 1 && page !== 1) {
      setPage(1);
    }

    dispatch(
      getScooterNotesAsync({
        page: effectivePage,
        limit,
        ...filters,
        cityId: adminInfo.currentCity
      })
    );
  }, [page, limit, filters, adminInfo.currentCity, dispatch]);

  const handleFormSubmit = (values: Partial<FilterHeaderValues>) => {
    const isValidDateRange =
      values.dateRange?.[0]?.isValid?.() && values.dateRange?.[1]?.isValid?.();

    const dateFrom = isValidDateRange
      ? values.dateRange![0]!.format(DATE_FORMAT)
      : undefined;

    const dateTo = isValidDateRange
      ? values.dateRange![1]!.format(DATE_FORMAT)
      : undefined;

    const dateObj = isValidDateRange
      ? {
          dateTo: dateTo as string,
          dateFrom: dateFrom as string
        }
      : {};

    setFilters({
      scooterName: values?.scooterName?.length ? values.scooterName : undefined,
      adminLogin: values?.adminLogin?.length ? values.adminLogin : undefined,
      ...dateObj
    });
  };

  return (
    <Root>
      <Title>{t('notes.title')}</Title>

      <FilterSection>
        <FilterHeader
          initialValues={initialValues}
          onSubmit={handleFormSubmit}
        />
      </FilterSection>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {!items || items.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('notes.emptyState')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t('notes.colType')}</TableHeaderCell>
                <TableHeaderCell>{t('notes.colScooter')}</TableHeaderCell>
                <TableHeaderCell>{t('notes.colAuthor')}</TableHeaderCell>
                <TableHeaderCell>{t('notes.colText')}</TableHeaderCell>
                <TableHeaderCell>{t('notes.colCreated')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map(note => (
                <StyledTableRow key={note._id}>
                  <StyledTableCell>
                    <IconWrapper>
                      <StickyNote2 sx={{ fontSize: 24, color: '#2196F3' }} />
                    </IconWrapper>
                  </StyledTableCell>
                  <StyledTableCell>
                    {note?.scooter?.name ?? '-'}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person sx={{ fontSize: 18, color: '#5073F8' }} />
                      {note.adminLogin}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    {note.text}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday sx={{ fontSize: 18, color: '#4CAF50' }} />
                      {moment(note.createdAt).format('DD.MM.YYYY HH:mm:ss')}
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {items && items.length > 0 && (
        <PaginationWrapper>
          <Pagination count={total} page={page} onChange={onChangePage} />
        </PaginationWrapper>
      )}
    </Root>
  );
};

export default NotesPage;
